import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Avatar, Box, Chip, LinearProgress, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import {
  acme,
  acmeBillingSectionContentSx,
  acmePageSectionCardSx,
} from "../../theme/acmeTheme";
import { Card, CardContent } from "../ui";
import axios from "axios";
import { isAdminRole } from "../../utils/userRoles";

const theme = {
  success: acme.colors.brand,
  warning: acme.colors.warning,
  error: acme.colors.destructive,
  border: acme.colors.border,
};

function formatCredits(value) {
  return Math.max(0, Math.floor(Number(value) || 0)).toLocaleString();
}

function formatEur(value) {
  const n = Number(value) || 0;
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    }).format(n);
  } catch {
    return `€${n.toFixed(2)}`;
  }
}

function normalizeWallet(data) {
  if (!data) return null;
  const credits = Math.max(
    0,
    Math.floor(Number(data.smsCredits ?? data.credits) || 0),
  );
  const peak = Math.max(
    credits,
    Math.floor(Number(data.smsCreditsPeak ?? data.creditsPeak) || 0),
  );
  return {
    credits,
    peak,
    creditValueEur: Number(data.creditValueEur) || 0,
    creditsEur:
      data.smsCreditsEur ??
      data.creditsEur ??
      credits * (Number(data.creditValueEur) || 0),
    unlimitedSms: !!data.unlimitedSms,
    planName: data.planName || "",
  };
}

/**
 * SMS credits usage bar — mirrors the email plan strip on campaign pages.
 * variant="card" for campaign pages, variant="inline" for Billing → Account usage.
 */
export default function SmsCreditsUsageBar({
  user,
  creditsInfo = null,
  variant = "card",
  sx = {},
}) {
  const navigate = useNavigate();
  const [wallet, setWallet] = useState(() => normalizeWallet(creditsInfo));
  const [loading, setLoading] = useState(!creditsInfo);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get("/api/sms-campaigns/credits");
      setWallet(normalizeWallet(data));
    } catch {
      setWallet(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (creditsInfo) {
      setWallet(normalizeWallet(creditsInfo));
      setLoading(false);
      return undefined;
    }
    load();
    return undefined;
  }, [creditsInfo, load]);

  const stats = useMemo(() => {
    if (!wallet) return null;
    if (wallet.unlimitedSms) {
      return {
        unlimited: true,
        statusBackground: theme.success,
        statusText: "Unlimited SMS",
        StatusIcon: CheckCircleIcon,
      };
    }

    const { credits, peak } = wallet;
    const remainingPercent =
      peak > 0 ? Math.min(100, (credits / peak) * 100) : credits > 0 ? 100 : 0;
    const depleted = credits <= 0;
    const low = !depleted && remainingPercent <= 10;

    let statusBackground = theme.success;
    let statusText = "SMS credits available";
    let StatusIcon = CheckCircleIcon;

    if (depleted) {
      statusBackground = theme.error;
      statusText = "No SMS credits";
      StatusIcon = WarningIcon;
    } else if (low) {
      statusBackground = theme.warning;
      statusText = "SMS credits running low";
      StatusIcon = WarningIcon;
    }

    return {
      unlimited: false,
      remainingPercent,
      depleted,
      low,
      statusBackground,
      statusText,
      StatusIcon,
    };
  }, [wallet]);

  if (user && isAdminRole(user.role)) return null;
  if (loading || !wallet || !stats) return null;

  const goToTopUp = () => navigate("/billing", { state: { activeTab: 2 } });

  const barColor = stats.depleted
    ? theme.error
    : stats.low
      ? theme.warning
      : theme.success;

  const progressValue = stats.unlimited ? 100 : stats.remainingPercent;

  const titleRow = (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        mb: variant === "card" ? 2 : 1,
      }}
    >
      <Avatar
        sx={{
          bgcolor: acme.colors.brandMuted,
          mr: 2,
          width: variant === "card" ? 48 : 40,
          height: variant === "card" ? 48 : 40,
        }}
      >
        <stats.StatusIcon
          sx={{
            color: stats.depleted
              ? theme.error
              : stats.low
                ? theme.warning
                : acme.colors.brand,
            fontSize: variant === "card" ? "1.5rem" : "1.25rem",
          }}
        />
      </Avatar>
      <Box sx={{ flex: 1 }}>
        <Typography
          variant={variant === "card" ? "h6" : "subtitle1"}
          sx={{
            color: acme.colors.foreground,
            fontWeight: 600,
          }}
        >
          {stats.statusText}
          {wallet.unlimitedSms && wallet.planName ? `: ${wallet.planName}` : ""}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: acme.colors.mutedForegroundSolid }}
        >
          {wallet.unlimitedSms
            ? "Your plan includes unlimited SMS sending."
            : stats.depleted
              ? "Buy credits in Billing → SMS Marketing Plan to send campaigns."
              : `Prepaid credits for SMS campaigns • Top up in Billing → SMS Marketing Plan`}
        </Typography>
      </Box>
      {!wallet.unlimitedSms && stats.depleted && variant === "card" && (
        <Chip
          label="Top up required"
          size="small"
          onClick={goToTopUp}
          sx={{ cursor: "pointer" }}
        />
      )}
    </Box>
  );

  const usageRow = !wallet.unlimitedSms ? (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Typography
          variant="body1"
          sx={{
            color: acme.colors.foreground,
            fontWeight: 500,
          }}
        >
          {formatCredits(wallet.credits)} / {formatCredits(wallet.peak)} credits
          remaining
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: acme.colors.mutedForegroundSolid }}
        >
          ≈ {formatEur(wallet.creditsEur)}
        </Typography>
      </Box>
      <Box
        sx={{
          backgroundColor: acme.colors.muted,
          borderRadius: acme.radius.sm,
          p: 0.5,
        }}
      >
        {variant === "inline" ? (
          <LinearProgress
            variant="determinate"
            value={progressValue}
            sx={{
              height: 10,
              borderRadius: 1,
              backgroundColor: "#e2e8f0",
              "& .MuiLinearProgress-bar": {
                backgroundColor: barColor,
              },
            }}
          />
        ) : (
          <Box
            sx={{
              height: 8,
              borderRadius: 1,
              width: `${progressValue}%`,
              bgcolor: barColor,
              transition: "width 0.3s ease",
            }}
          />
        )}
      </Box>
      {variant === "inline" && (
        <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
          {Math.round(progressValue)}% of your last top-up balance remaining
          {stats.depleted && (
            <span style={{ color: theme.error, fontWeight: "bold" }}>
              {" "}
              (no credits — top up to send)
            </span>
          )}
          {stats.low && !stats.depleted && (
            <span style={{ color: theme.warning, fontWeight: "bold" }}>
              {" "}
              (running low)
            </span>
          )}
        </Typography>
      )}
    </>
  ) : (
    <Typography
      variant="body2"
      sx={{ color: acme.colors.mutedForegroundSolid }}
    >
      Unlimited usage
    </Typography>
  );

  if (variant === "inline") {
    return (
      <Card sx={{ ...acmePageSectionCardSx, boxShadow: "none", mt: 2, ...sx }}>
        <CardContent sx={acmeBillingSectionContentSx}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              fontWeight: 600,
              color: acme.colors.foreground,
            }}
          >
            SMS credits
          </Typography>
          {wallet.unlimitedSms ? (
            <>
              <Typography
                variant="body1"
                gutterBottom
                sx={{ color: acme.colors.foreground }}
              >
                Plan: {wallet.planName || "Unlimited"}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: acme.colors.mutedForegroundSolid }}
              >
                Unlimited SMS usage
              </Typography>
            </>
          ) : (
            <>
              <Typography
                variant="body1"
                gutterBottom
                sx={{ color: acme.colors.foreground }}
              >
                Credits remaining: {formatCredits(wallet.credits)} /{" "}
                {formatCredits(wallet.peak)} (≈ {formatEur(wallet.creditsEur)})
              </Typography>
              <Box sx={{ width: "100%", my: 2 }}>
                <LinearProgress
                  variant="determinate"
                  value={progressValue}
                  sx={{
                    height: 10,
                    borderRadius: acme.radius.sm,
                    bgcolor: acme.colors.muted,
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: barColor,
                    },
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{ mt: 1, color: acme.colors.mutedForegroundSolid }}
                >
                  {Math.round(progressValue)}% of your last top-up balance
                  remaining
                  {stats.depleted && (
                    <Box
                      component="span"
                      sx={{ color: theme.error, fontWeight: 600, ml: 0.5 }}
                    >
                      (no credits — top up to send)
                    </Box>
                  )}
                  {stats.low && !stats.depleted && (
                    <Box
                      component="span"
                      sx={{ color: theme.warning, fontWeight: 600, ml: 0.5 }}
                    >
                      (running low)
                    </Box>
                  )}
                </Typography>
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        mb: 3,
        boxShadow: "none",
        border: `1px solid ${theme.border}`,
        ...(stats.depleted || stats.low
          ? {
              borderColor: stats.depleted ? theme.error : theme.warning,
            }
          : {
              ...acmePageSectionCardSx,
            }),
        ...sx,
      }}
    >
      <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
        {titleRow}
        {usageRow}
      </CardContent>
    </Card>
  );
}
